import { useState } from "react";
import ScreensaverView from "./ScreensaverView";
import useCustomEffect from "../../../hooks/useCustomEffect";
import { useRef } from "react";
import { useParameterContext } from "../../../context/ParameterContext";
import useScannerListener from "../../../hooks/useScannerListener";
import { useAuthContext } from "../../../context/AuthContext";
import { useLanguageContext } from "../../../context/LanguageContext";

const ACTIVITY_EVENTS = [
  "click",
  "keypress",
  "pointerdown",
  "touchstart",
  "scroll",
];

function Screensaver({ screensavers = [], onActivation, onDeactivation }) {
  const authContext = useAuthContext();
  const parameterContext = useParameterContext();
  const { translateEntity } = useLanguageContext();
  const switchTimer = useRef(null);
  const activateTimer = useRef(null);
  const screensaverRef = useRef(null);
  const [data, setData] = useState({
    currentIndex: 0,
    initialActivation: true,
    showPrevious: false,
    disabled:
      authContext.auth.impersonating && localStorage.getItem("bc_terminal_id"),
  });

  useScannerListener(onScreensaverClick);

  // Set the current screensaver
  const currentScreensaver =
    screensavers.length > data.currentIndex
      ? screensavers[data.currentIndex]
      : null;

  // Set the previous screensaver
  const previousScreensaver =
    screensavers.length > getPreviousIndex() &&
    data.showPrevious &&
    getPreviousIndex() !== data.currentIndex
      ? screensavers[getPreviousIndex()]
      : null;

  /**
   * UseEffect for initializations and cleanup
   */
  useCustomEffect(() => {
    //Capture, so scrolling inside an element also counts (scroll events don't bubble)
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, resetActivateTimer, true),
    );

    return () => {
      clearInterval(switchTimer.current);
      clearTimeout(activateTimer.current);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, resetActivateTimer, true),
      );
    };
  });

  /**
   * UseEffect for activating and deactivating the screensaver. The screensaver also activates without
   * screensavers, so it is the one place that determines whether the application is idle
   */
  useCustomEffect(() => {
    if (data.disabled) {
      onCompDeactivation();
    } else {
      onCompActivation();
    }
  }, [data.disabled, screensavers]);

  /**
   * This method will activate the switch interval for the screensavers
   */
  function startSwitchTimer() {
    clearInterval(switchTimer.current);

    if (screensavers.length === 0) {
      return;
    }

    switchTimer.current = setInterval(() => {
      setData((prev) => ({
        ...prev,
        showPrevious: true,
        currentIndex:
          prev.currentIndex + 1 >= screensavers.length
            ? 0
            : prev.currentIndex + 1,
      }));
    }, getSwitchTimer());
  }

  /**
   * This method will activate the timer for the screensavers
   */
  function startActivateTimer() {
    clearTimeout(activateTimer.current);

    activateTimer.current = setTimeout(() => {
      //Someone is watching a video (for example the house rules), so the application isn't idle
      if (isVideoPlaying()) {
        startActivateTimer();
        return;
      }

      setData((prev) => ({
        ...prev,
        disabled: false,
        currentIndex: 0,
        showPrevious: false,
      }));
    }, getActivateTimer());
  }

  /**
   * This method will check if a video on the page is playing
   * @returns {boolean}
   */
  function isVideoPlaying() {
    return Array.from(document.querySelectorAll("video")).some(
      (video) => !video.paused && !video.ended,
    );
  }

  /**
   * This method will deactivate the screensaver and reset the activate timer on user interaction
   * @param {Event} event
   */
  function resetActivateTimer(event) {
    //A press on the visible overlay is handled by its own click. Deactivating on pointerdown/touchstart
    //would make the overlay click-through before the press ends, so the click lands on what is under it
    const onOverlay =
      event?.target instanceof Node &&
      screensaverRef.current?.contains(event.target);

    //Without screensavers there is no overlay to click, so the interaction itself deactivates it
    if (!onOverlay) {
      setData((prev) => (prev.disabled ? prev : { ...prev, disabled: true }));
    }

    if (activateTimer.current) {
      startActivateTimer();
    }
  }

  /**
   * This method will return the activate timer for the screensavers
   * @returns {number}
   */
  function getActivateTimer() {
    return (
      (parameterContext.getParameter("screensaver.timer.activate")?.value ??
        30) * 1000
    );
  }

  /**
   * This method will return the switch timer for the screensavers
   * @returns {number}
   */
  function getSwitchTimer() {
    return (
      (parameterContext.getParameter("screensaver.timer.switch")?.value ?? 10) *
      1000
    );
  }

  /**
   * This method will return the previous screensaver index
   * @returns {number}
   */
  function getPreviousIndex() {
    return data.currentIndex - 1 < 0
      ? screensavers.length - 1
      : data.currentIndex - 1;
  }

  /**
   * This method will return the path of the given screensaver file. Files with
   * use_translations have no path of their own, so it is taken from the translation
   * @param {object|null} screensaver
   * @returns {string|null}
   */
  function getScreensaverPath(screensaver) {
    if (!screensaver) {
      return null;
    }

    const path = translateEntity(screensaver, "path");
    return typeof path === "string" && path ? path : null;
  }

  /**
   * This method will return the fade time for the screensavers
   * @returns {number}
   */
  function getFadeTime() {
    return Math.min(1500, getSwitchTimer());
  }

  /**
   * This method will be called when the screensaver is clicked
   */
  function onScreensaverClick() {
    setData((prev) => ({
      ...prev,
      disabled: true,
    }));
  }

  /**
   * This method will be called when the component is activated
   */
  function onCompActivation() {
    if (
      authContext.auth.impersonating &&
      localStorage.getItem("bc_terminal_id") &&
      authContext.auth.user.company.type !== "business_center"
    ) {
      authContext.logout();
    }

    startSwitchTimer();

    if (onActivation) {
      onActivation(data.initialActivation);
    }

    setData((prev) => ({
      ...prev,
      initialActivation: false,
    }));
  }

  /**
   * This method will be called when the component is deactivated
   */
  function onCompDeactivation() {
    clearInterval(switchTimer.current);
    startActivateTimer();

    if (onDeactivation) {
      onDeactivation();
    }
  }

  return (
    <ScreensaverView
      data={data}
      screensaverRef={screensaverRef}
      fadeTime={getFadeTime()}
      currentScreensaver={currentScreensaver}
      previousScreensaver={previousScreensaver}
      currentScreensaverPath={getScreensaverPath(currentScreensaver)}
      previousScreensaverPath={getScreensaverPath(previousScreensaver)}
      onScreensaverClick={onScreensaverClick}
    />
  );
}

export default Screensaver;
